import { Link as RouterLink } from 'react-router';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { styled } from '@mui/system';
import Avatar from '@components/Avatar';
import { CssRating, StyledUserIcon } from '@components/ResponseItem/styles';
import { Typography } from '@components/Typography';
import { RoutePath } from '@constants/routes';
import { formatDateOrEmpty } from '@helpers/dates';
import { getIntegrationIcon } from '@helpers/integrations';
import { getInitialsFull } from '@helpers/userName';
import { useTranslation } from '@hooks';

const Card = styled(RouterLink)({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '16px',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid var(--divider)',
    backgroundColor: '#fff',
    color: 'inherit',
    textDecoration: 'none',
    transition: 'background-color 0.15s',
    '&:hover': {
        backgroundColor: 'var(--action-hover)',
    },
});

const HeaderRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
});

const Author = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
});

const AuthorDetails = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    wordBreak: 'break-word',
});

const MetaRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
});

const ReviewText = styled(Typography)({
    whiteSpace: 'pre-line',
});

const LinkedReview = ({ review, language }) => {
    const { t } = useTranslation();
    const date = formatDateOrEmpty(review?.createdAt, 'd MMM yyyy', language);

    return (
        <Card
            to={`${RoutePath.ReviewsManage}?reviewId=${review.reviewId}`}
            aria-label={t('FeedbackResponses.linkedReview')}
        >
            <HeaderRow>
                <Author>
                    <Avatar
                        size="medium"
                        fullName={review?.userName}
                        image={review?.userPhotoUrl}
                        label={getInitialsFull(review?.userName)}
                        icon={<StyledUserIcon />}
                        withBadge
                        badge={getIntegrationIcon(review?.platform)}
                    />
                    <AuthorDetails>
                        <Typography variant="subtitle2" color="textPrimary">
                            {review?.userName}
                        </Typography>
                        <MetaRow>
                            <CssRating
                                size="small"
                                name="linked-review-rating"
                                value={review?.rating || 0}
                                readOnly
                                icon={<StarRoundedIcon fontSize="inherit" />}
                                emptyIcon={<StarRoundedIcon fontSize="inherit" sx={{ color: '#E4EAEB' }} />}
                            />
                            {date && (
                                <Typography variant="caption" color="textSecondary">
                                    {date}
                                </Typography>
                            )}
                        </MetaRow>
                    </AuthorDetails>
                </Author>
                <ChevronRightRoundedIcon sx={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
            </HeaderRow>
            {review?.text && (
                <ReviewText variant="body2" color="textSecondary">
                    {review.text}
                </ReviewText>
            )}
        </Card>
    );
};

export default LinkedReview;
